import { useLazyQuery, useReactiveVar } from '@apollo/client';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import type { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect } from 'react';
import { parseViewFieldValues } from 'src/api/viewModels/data';
import { buildViewFields } from 'src/api/viewModels/queryWrappers';
import viewStateVar from 'src/store/viewState';

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    color: theme.palette.text.disabled,
    width: '100%',
  },
  value: {
    width: '100%',
    wordWrap: 'break-word',
  },
}));

interface ExtendedMetaDataCardProps {
  itemid: string;
}

interface ViewField {
  fieldLabel?: string;
  show: string | null;
}

const ExtendedMetaDataCard: React.FC<ExtendedMetaDataCardProps> = ({ itemid }) => {
  const classes = useStyles();
  const viewState = useReactiveVar(viewStateVar);

  const ViewMediaCardShowMetadata = viewState.viewModels?.library?.ViewMediaCardShowMetadata;
  const [getViewFields, { data, error, loading }] = useLazyQuery(buildViewFields(), {
    variables: {
      globalId: itemid,
      view_ComponentName: ViewMediaCardShowMetadata?.node?.componentName,
    },
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'cache-only',
  });

  useEffect(() => {
    if (viewState.showMetadata) {
      getViewFields();
    }
  }, [viewState.showMetadata, getViewFields]);

  if (loading) {
    return (
      <CardContent>
        <CircularProgress color="secondary" />
      </CardContent>
    );
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  const viewFields = parseViewFieldValues(data as Record<string, unknown>) as ViewField[] | undefined;

  return (
    <CardContent>
      {viewFields?.map((field, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: view fields have no stable unique identifier
        <React.Fragment key={index}>
          <Typography className={classes.title}>{field.fieldLabel}:</Typography>
          <Typography paragraph className={classes.value}>
            {field.show}
          </Typography>
        </React.Fragment>
      ))}
    </CardContent>
  );
};

export default ExtendedMetaDataCard;
